import type { Finding } from './api.ts'
import {
  activesBoard,
  groupByItem,
  worstStatus,
  type VerificationItem,
  type ColumnStatus,
  type Side,
} from './verificationItems.ts'

/**
 * The guided pass: one step per entry, in prescription order.
 *
 * **Every entry, including the clean ones.** This is the correction that
 * matters most in porting V1's pass. V2's guided mode showed only findings that
 * still needed a decision, on the reasoning that walking somebody through
 * fourteen "this was fine" screens is how a guided mode becomes the thing
 * people skip. V1 does the opposite on purpose, and says why:
 *
 *   > pharmacists are legally required to review every item, so the sequence
 *   > includes the clean ones.
 *
 * That is not a UX preference to weigh against ours. A pass that silently skips
 * items cannot be the record that they were reviewed, and being that record is
 * what this screen is for.
 *
 * The order is V1's: patient, prescriber, then each preparation, then anything
 * requisition-wide. A step is an *entry*, never a single finding -- a
 * preparation is reviewed as a preparation, with its form, ativos, posologia
 * and volume in front of the pharmacist at once.
 */

export type GuidedStepKind = 'identity' | 'item' | 'unquoted' | 'unmatched' | 'requisition'

export interface GuidedStep {
  /** Stable across reloads, so progress can key on it. */
  readonly id: string
  readonly kind: GuidedStepKind
  readonly title: string
  /** Said beside the title, where the title alone would repeat another step's. */
  readonly badge?: string
  readonly status: ColumnStatus
  readonly findings: readonly Finding[]
  /** The preparation, on item steps: the ATIVOS board is a list of it. */
  readonly item?: VerificationItem
  /** Blocks to frame on the prescription. Empty degrades gracefully. */
  readonly regions: readonly string[]
  /**
   * The one finding this step is about, on the divergence-only pass (#641).
   * Absent on the full pass, whose steps are entries.
   */
  readonly divergence?: Finding
  /** The full pass's step it was cut from, so switching passes keeps the place. */
  readonly parentId?: string
}

/**
 * Who it is for and who wrote it, on one screen.
 *
 * They were two steps, and answering them is one act: a pharmacist checks the
 * top of the receita against the top of the orçamento, and the two names are
 * inches apart on both. Splitting that into two passes made somebody read the
 * same corner of the same page twice, and the second step arrived with nothing
 * new on it.
 *
 * `Dados cadastrais` is the label, which is also what the pharmacy calls this
 * part of a requisição.
 */
const IDENTITY_CHECKS = ['patient', 'prescriber', 'registration'] as const

function identityRegions(prescription: Side | undefined): readonly string[] {
  return [...(prescription?.patientBlocks ?? []), ...(prescription?.prescriberBlocks ?? [])]
}

/**
 * @param prescription where the identity steps point their highlights
 * @param quotation the keyed items, which are the steps in between
 */
export function buildGuidedSteps(
  prescription: Side | undefined,
  quotation: Side | undefined,
  findings: readonly Finding[],
): GuidedStep[] {
  const grouped = groupByItem(quotation, findings, new Set(), prescription)
  const steps: GuidedStep[] = []

  const identity = grouped.document.filter((f) =>
    (IDENTITY_CHECKS as readonly string[]).includes(f.check),
  )
  // A check that produced nothing is still a step: the pass is the record
  // that a person looked, and an entry that vanishes was never reviewed.
  steps.push({
    id: 'identity:document',
    kind: 'identity',
    title: 'Dados cadastrais',
    status: worstStatus(identity),
    findings: identity,
    // From the document, not the findings: a name that agrees produces a
    // finding with no citation, and the step still has to frame the names it
    // is asking about.
    regions: [
      ...identityRegions(prescription),
      // The registration's own citation (#277), which the document side does
      // not carry: it is read off the page beside the prescriber's name.
      ...identity.filter((f) => f.check === 'registration').flatMap((f) => f.regions),
    ],
  })

  /*
   * One step per keyed item, its rótulo included.
   *
   * The rótulo had a step of its own after its formula, compared against the
   * receita (#454, #464). It is back inside the formula's step, shown for the
   * conferente to check by eye and compared with nothing (#438), so it adds
   * no step and no finding.
   */
  for (const item of grouped.items) {
    steps.push({
      id: `item:${item.preparationId}`,
      kind: 'item',
      title: `${item.ordinal}) ${item.title}`,
      status: worstStatus(item.findings),
      findings: item.findings,
      item,
      regions: item.findings.flatMap((f) => f.regions),
    })
  }

  /*
   * What the receita asked for and the pharmacy never keyed: one step, last.
   *
   * These were one count at the bottom first -- "five prescribed items
   * missing" -- which told a pharmacist nothing she could act on, and then a
   * step apiece, which named each one but scattered them through the pass as
   * though each were a difference to answer. Most are a customer who did not
   * want that formula.
   *
   * So they are gathered, at the end, and answered by silence: the findings
   * are `info` now and nothing has to be said about them. A conferente who
   * means to key one still says so on its row, and that alone sends the
   * conferência for correction.
   *
   * A preparation of which some ingredient *was* keyed is not here: it rides
   * on that item, and its leftovers are a composition finding there.
   */
  if (grouped.unquoted.length > 0) {
    steps.push({
      id: 'unquoted',
      kind: 'unquoted',
      title:
        grouped.unquoted.length === 1 ? 'Não orçado' : `Não orçados (${grouped.unquoted.length})`,
      status: worstStatus(grouped.unquoted),
      findings: grouped.unquoted,
      regions: grouped.unquoted.flatMap((f) => f.regions),
    })
  }

  if (grouped.orphans.length > 0) {
    steps.push({
      id: 'unmatched',
      kind: 'unmatched',
      title: 'Itens não pareados',
      status: worstStatus(grouped.orphans),
      findings: grouped.orphans,
      regions: grouped.orphans.flatMap((f) => f.regions),
    })
  }

  /*
   * Requisition-wide checks come last, as their own step, so nothing rides
   * inside an item it does not belong to.
   *
   * Only when there is something to answer. A step whose every finding came
   * back `ok` has nothing in it, and it was landing at the end of the pass as
   * an empty page carrying the Concluir button -- so the last thing somebody
   * did before signing off was read a screen that said nothing.
   *
   * Deliberately different from the identity step above, which is always
   * drawn: that one is the record that a person looked, and a disappearing
   * entry would be one nobody reviewed. This step is not part of that record
   * -- it exists to carry findings, so with none worth showing it should not
   * exist.
   */
  const wide = grouped.document.filter(
    (f) => !(IDENTITY_CHECKS as readonly string[]).includes(f.check),
  )
  if (wide.some((f) => f.disposition !== 'ok')) {
    steps.push({
      id: 'requisition',
      kind: 'requisition',
      // The operational rules about the requisition as a whole -- an expired
      // controlled receita, a veterinary one without the animal's weight.
      // Nothing about the preparations lives here any more.
      title: 'Regras do pedido',
      status: worstStatus(wide),
      findings: wide,
      regions: wide.flatMap((f) => f.regions),
    })
  }

  return steps
}

/**
 * The colour of a dot on the rail, which comes from the step's status alone.
 *
 * Checked against the live V1: an unvisited step with nothing wrong is drawn in
 * brand blue, not grey. Whether somebody has *been* there is carried by the
 * fill and the tick, not by the colour -- so the rail reads as two facts at
 * once, what each step concluded and how far along the pass is, instead of
 * collapsing them into one.
 */
export type RailTone = 'error' | 'clean'

export function railTone(step: GuidedStep): RailTone {
  return step.status === 'error' ? 'error' : 'clean'
}

export interface RailCounts {
  readonly reviewed: number
  readonly total: number
  readonly flagged: number
  readonly notEvaluated: number
  readonly remaining: number
}

export function railCounts(
  steps: readonly GuidedStep[],
  visited: ReadonlySet<number>,
  current: number,
): RailCounts {
  // The step somebody is standing on is not yet reviewed -- they are looking
  // at it. Counting it would let the rail reach "N de N" before the last
  // decision was made.
  const reviewed = [...visited].filter((i) => i !== current && i < steps.length).length
  return {
    reviewed,
    total: steps.length,
    flagged: steps.filter((s) => s.status === 'error').length,
    notEvaluated: 0,
    remaining: steps.length - reviewed,
  }
}

/** V1's counter sentence: "2 de 7 revisados · 5 sinalizados · 5 restantes". */
export function railCounterText(counts: RailCounts): string {
  const parts = [`${counts.reviewed} de ${counts.total} revisados`]
  if (counts.flagged > 0) {
    parts.push(counts.flagged === 1 ? '1 sinalizado' : `${counts.flagged} sinalizados`)
  }
  if (counts.remaining > 0) {
    parts.push(counts.remaining === 1 ? '1 restante' : `${counts.remaining} restantes`)
  }
  return parts.join(' · ')
}

/**
 * The row a step should arrive already pointing at, when there is one.
 *
 * **Only ever something flagged.** A step where nothing is wrong opens with
 * nothing selected: pre-selecting a clean row would put a rectangle on the
 * page and an outline on a row to announce that they agree, which is a claim
 * on somebody's attention in exchange for nothing. Every row is still one
 * click from being shown -- that is what the eye is for.
 *
 * Ingredients come first because a preparation's divergence is nearly always
 * about one of them, and an ingredient row is the smallest true target on the
 * page. Then the check boards.
 */
export interface OpenRow {
  readonly key: string
  readonly regions: readonly string[]
  /** What the row concluded, so the rectangle and the outline agree. */
  readonly tone: 'error' | 'brand'
}

export function openingRow(step: GuidedStep): OpenRow | undefined {
  // Only a formula's step draws ingredient rows.
  const rows = step.kind !== 'item' || step.item === undefined ? [] : activesBoard(step.item).rows

  const divergent = rows.find((r) => r.status === 'error' && r.regions.length > 0)
  if (divergent !== undefined) {
    return { key: divergent.key, regions: divergent.regions, tone: 'error' }
  }

  /*
   * Composition is never a target of its own.
   *
   * It is drawn as the ingredient list, and the list's selectable units are
   * its rows -- there is no board for it to own. Selecting it anyway drew
   * rectangles on the image while nothing on the comparison side was marked,
   * because its key matches no row, and the two never looked like they were
   * about each other. What it cites belongs to the ingredients it cites it
   * for, and those are rows.
   */
  const board = step.findings.find(
    (f) => f.regions.length > 0 && f.check !== 'composition' && f.disposition === 'divergence',
  )

  if (board !== undefined) {
    return { key: board.fingerprint, regions: board.regions, tone: 'error' }
  }

  /*
   * Nothing flagged, nothing pinned -- however few candidates the step has.
   *
   * A step with a single clean field used to open with it pinned, and so did
   * Dados cadastrais with both names. A pin wears the brand bar and wash, so a
   * correct patient arrived blue while a correct ingredient next door arrived
   * white: the same state, two looks, and the blue read as "confirmed" when it
   * only meant "we chose this for you" (#334). The panes still agree -- no
   * rectangle is drawn for a row nobody pinned.
   */
  return undefined
}

/**
 * Every row a step arrives pinned on.
 *
 * The same rule everywhere: only what diverges. A preparation opens on its one
 * divergent row, as `openingRow` picks it. Dados cadastrais opens on each
 * name that diverges -- both if both do, since the two sit inches apart and
 * are answered together -- and on neither when both agree.
 */
export function openingPins(step: GuidedStep): readonly OpenRow[] {
  // A step about one divergence opens on that one, whatever else its row holds.
  if (step.divergence !== undefined) return pinsFor(step, step.divergence)
  if (step.kind === 'identity') {
    return step.findings
      .filter((f) => f.regions.length > 0 && f.disposition === 'divergence')
      .map((f) => ({ key: f.fingerprint, regions: f.regions, tone: 'error' as const }))
  }
  const row = openingRow(step)
  return row === undefined ? [] : [row]
}

/**
 * Whether a link names anything to open on.
 *
 * Feedback about the verification as a whole -- a comment -- names no finding
 * and no pote. Looking for its place would always miss, and the screen would
 * say a finding is gone when there never was one.
 */
export function namesFinding(
  fingerprint: string | undefined,
  near: { readonly preparation?: string | undefined } | undefined,
): boolean {
  return fingerprint !== undefined || near?.preparation !== undefined
}

/**
 * Where a link to one finding opens: its step, and its row pinned.
 *
 * For a link that has to show a single case -- the feedback acompanhamento
 * sends someone to the exact apontamento a conferente contested -- rather
 * than the conferência from the top. On a preparation the pinned unit is the
 * ingredient row the finding rides on, as it is when somebody clicks it;
 * elsewhere it is the finding's own board.
 *
 * The fingerprint is a hash of the finding's content: a re-run that says the
 * same thing keeps it, one that says something else does not. Then `near`
 * (the pote and the check it was on) opens the same place with `exact: false`,
 * and the screen says so. Undefined when neither is there any more.
 */
export function openingForFinding(
  steps: readonly GuidedStep[],
  fingerprint: string | undefined,
  near: { readonly preparation?: string | undefined; readonly check?: string | undefined } = {},
):
  | { readonly index: number; readonly pins: readonly OpenRow[]; readonly exact: boolean }
  | undefined {
  const exact = steps.findIndex((step) =>
    step.findings.some((finding) => finding.fingerprint === fingerprint),
  )
  if (exact !== -1) {
    const finding = steps[exact]!.findings.find((one) => one.fingerprint === fingerprint)!
    return { index: exact, pins: pinsFor(steps[exact]!, finding), exact: true }
  }

  /*
   * Changed since: the same check on the same pote, as near as the link can
   * get. The pote's id is its position on the orçamento, which a re-run keeps.
   */
  if (near.preparation === undefined) return undefined
  const index = steps.findIndex(
    (step) => step.kind === 'item' && step.item?.preparationId === near.preparation,
  )
  if (index === -1) return undefined
  const step = steps[index]!
  const same = step.findings.find((one) => one.check === near.check)
  return { index, pins: same === undefined ? [] : pinsFor(step, same), exact: false }
}

function pinsFor(step: GuidedStep, finding: Finding): OpenRow[] {
  const fingerprint = finding.fingerprint
  const tone = finding.disposition === 'divergence' ? ('error' as const) : ('brand' as const)

  if (step.kind === 'item' && step.item !== undefined) {
    const row = activesBoard(step.item).rows.find((one) =>
      one.findings.some((f) => f.fingerprint === fingerprint),
    )
    if (row !== undefined) return [{ key: row.key, regions: row.regions, tone }]
  }
  return [{ key: fingerprint, regions: finding.regions, tone }]
}

/**
 * Steps still holding a finding nobody has answered.
 *
 * The completion guard is built on this, and so is the jump it offers. Derived
 * from the same decision map the cards read, so a pharmacist can never be told
 * something is outstanding while the step in front of them shows it decided.
 *
 * Matches the review gate's own rule: only `divergence` and `undecided` are
 * work. `info` is something we noticed and `ok` is something we checked.
 */
/**
 * How many findings still need an answer, across the whole conferência.
 *
 * Findings, not steps. `unresolvedStepIndexes` answers "where do I send them",
 * which is a different question and a smaller number -- one preparation holding
 * two unanswered divergences is a single step. Telling somebody "1 item ainda
 * sem decisão" while the screen in front of them shows two is a screen
 * arguing with itself, and it is also what the server counts when it refuses
 * to conclude.
 */
export function unresolvedFindingCount(
  steps: readonly GuidedStep[],
  answered: ReadonlySet<string>,
): number {
  const seen = new Set<string>()
  for (const step of steps) {
    for (const finding of step.findings) {
      if (finding.disposition !== 'divergence' && finding.disposition !== 'undecided') continue
      if (answered.has(finding.fingerprint)) continue
      // De-duplicated: a finding can appear in more than one step's view, and
      // counting it twice would overstate the work left.
      seen.add(finding.fingerprint)
    }
  }
  return seen.size
}

export function unresolvedStepIndexes(
  steps: readonly GuidedStep[],
  answered: ReadonlySet<string>,
): readonly number[] {
  return steps
    .map((step, index) => ({ step, index }))
    .filter(({ step }) =>
      step.findings.some(
        (f) =>
          (f.disposition === 'divergence' || f.disposition === 'undecided') &&
          !answered.has(f.fingerprint),
      ),
    )
    .map(({ index }) => index)
}
